/**
 * Mentorship Guidelines Modal
 *
 * Fellows Corner counterpart to the Medical On Demand guidelines. Says what the
 * ask form is for and where the advice stops. Copy is overridable as a
 * Flagsmith value, falling back to the in-repo wording.
 *
 * Scope only. What a question should sound like is carried by the per-mentor
 * placeholder in the field itself, at the moment of writing, and what a full
 * exchange reads like by that mentor's own seed questions. A sample here would
 * be a third telling of the same thing, in the one place a fellow might never
 * open.
 */

import { X } from 'lucide-react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { useFellowsGuidelines } from '@/hooks/useFellowsGuidelines';

type MentorshipGuidelinesProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function MentorshipGuidelines({ isOpen, onClose }: MentorshipGuidelinesProps) {
  const { title, intro, sections, disclaimer } = useFellowsGuidelines();

  const size =
    useBreakpointValue({
      default: 'lg',
      md: 'md',
    }) || 'md';

  return (
    <Modal isOpen={isOpen} size={size} onClose={onClose}>
      <ModalBackdrop />

      <ModalContent className="max-h-[85vh] max-w-[700px]">
        <ModalHeader>
          <Heading size="h2-md">{title}</Heading>
          <ModalCloseButton>
            <Icon as={X} size="md" className="text-typography-500" />
          </ModalCloseButton>
        </ModalHeader>

        <Divider className="my-4" />

        <ModalBody className="py-3">
          <VStack space="lg">
            <Text className="text-typography-700">{intro}</Text>

            {sections.map((section, sectionIndex) => (
              <VStack key={sectionIndex} space="sm">
                <Heading size="sm">{section.heading}</Heading>
                <VStack space="xs">
                  {section.items.map((item, itemIndex) => (
                    <HStack key={itemIndex} space="sm" className="items-start">
                      <Text size="sm" className="text-typography-700">
                        •
                      </Text>
                      <Text size="sm" className="min-w-0 flex-1 text-typography-700">
                        {item}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            ))}

            <Text size="sm" className="text-typography-600">
              {disclaimer}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter className="my-3">
          <Button onPress={onClose} className="w-full md:w-auto">
            <ButtonText>Got it</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
